import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from 'recharts';
import { GpxPoint } from '../types/gpx';
import { simplifyGPXData } from '../utils/simplifyGPXData';
import './ElevationProfilePanel.css'; // We'll create this CSS file next

// Define type for the setter function prop
type SetHoveredLocation = React.Dispatch<React.SetStateAction<[number, number] | null>>;

// Interface for simplified data point including gain
interface SimplifiedPointWithGain extends GpxPoint {
    distance: number; // Cumulative distance from the start in meters
    cumulativeGain: number; // Cumulative elevation gain from the start in meters
}

interface ElevationProfilePanelProps {
    gpxPoints: GpxPoint[];
    isOpen: boolean;
    targetPoints?: number; // Optional prop to control simplification
    setHoveredLocation: SetHoveredLocation; // Add the setter prop
}

const ElevationProfilePanel: React.FC<ElevationProfilePanelProps> = ({ gpxPoints, isOpen, targetPoints = 1500, setHoveredLocation }) => {
    // Log the received points before simplification
    React.useEffect(() => {
        if (gpxPoints.length > 0) {
            console.log('ElevationProfilePanel received gpxPoints:', gpxPoints.slice(0, 10)); // Log first 10 points
            // Check if elevation values are varied
            const elevations = gpxPoints.map(p => p.ele);
            const uniqueElevations = Array.from(new Set(elevations)); // Use Array.from() to convert Set to Array
            console.log('Unique elevations received:', uniqueElevations.slice(0, 5)); // Log first 5 unique elevations
        }
    }, [gpxPoints]);

    const simplifiedData = React.useMemo(() => simplifyGPXData(gpxPoints, targetPoints), [gpxPoints, targetPoints]);

    // Calculate cumulative gain for the simplified data
    const dataWithGain = React.useMemo((): SimplifiedPointWithGain[] => {
        let gain = 0;
        return simplifiedData.map((point, index, arr) => {
            if (index > 0) {
                const diff = point.ele - arr[index - 1].ele;
                if (diff > 0) {
                    gain += diff;
                }
            }
            return { ...point, cumulativeGain: gain };
        });
    }, [simplifiedData]);

    const yDomain = React.useMemo(() => {
        if (simplifiedData.length === 0) return [0, 100]; // Default domain if simplification yields no data
        const elevations = simplifiedData.map(p => p.ele);
        const minEle = Math.min(...elevations);
        const maxEle = Math.max(...elevations);
        const padding = (maxEle - minEle) * 0.1;
        return [Math.max(0, Math.floor(minEle - padding)), Math.ceil(maxEle + padding)];
    }, [simplifiedData]);

    // Calculate totalDistance and markerReferenceDots *before* the early return
    const totalDistance = dataWithGain[dataWithGain.length - 1]?.distance || 0;
    const markerReferenceDots = React.useMemo(() => {
        // Ensure dataWithGain has data before proceeding
        if (dataWithGain.length === 0) return [];
        const distanceMarkers = [
            { distance: 0, label: 'Start' },
            { distance: totalDistance * 0.25, label: '25%' },
            { distance: totalDistance * 0.50, label: '50%' },
            { distance: totalDistance * 0.75, label: '75%' },
            { distance: totalDistance, label: 'End' }
        ];
        return distanceMarkers.map(marker => {
            let closestPoint = dataWithGain[0];
            let minDiff = Math.abs(closestPoint.distance - marker.distance);
    
            for (let i = 1; i < dataWithGain.length; i++) {
                const diff = Math.abs(dataWithGain[i].distance - marker.distance);
                if (diff < minDiff) {
                    minDiff = diff;
                    closestPoint = dataWithGain[i];
                }
            }
            return { ...closestPoint, label: marker.label };
        });
    }, [dataWithGain, totalDistance]); // Dependencies are correct

    // Now perform the early return check
    if (!isOpen || dataWithGain.length === 0) {
        return null; 
    }

    // Custom Tooltip Formatter - updated to show gain
    const renderTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data: SimplifiedPointWithGain = payload[0].payload;
            return (
                <div className="custom-tooltip">
                    <p className="label">{`Dist: ${(data.distance / 1000).toFixed(2)} km`}</p>
                    <p className="intro">{`Elev: ${data.ele.toFixed(1)} m`}</p>
                    <p className="intro">{`Gain: ${data.cumulativeGain.toFixed(0)} m`}</p> 
                </div>
            );
        }
        return null;
    };

    // Handlers for chart mouse events
    const handleChartMouseMove = (chartState: any) => {
        if (chartState && chartState.isTooltipActive && chartState.activePayload && chartState.activePayload.length > 0) {
            const pointData: SimplifiedPointWithGain = chartState.activePayload[0].payload;
            setHoveredLocation([pointData.lat, pointData.lon]);
        } else {
            setHoveredLocation(null);
        }
    };

    const handleChartMouseLeave = () => {
        setHoveredLocation(null);
    };

    return (
        <div className={`elevation-panel ${isOpen ? 'open' : ''}`}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={dataWithGain}
                    margin={{
                        top: 5,
                        right: 40,
                        left: 10,
                        bottom: 40
                    }}
                    onMouseMove={handleChartMouseMove}
                    onMouseLeave={handleChartMouseLeave}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                        dataKey="distance"
                        type="number"
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={(tick) => `${(tick / 1000).toFixed(1)}km`}
                        angle={-45}
                        textAnchor="end"
                        height={55}
                        dy={5}
                        tick={{ fontSize: 10 }}
                    />
                    <YAxis
                        domain={yDomain}
                        tickFormatter={(tick) => `${tick}m`}
                        allowDecimals={false}
                        width={50}
                        dx={-5}
                        tick={{ fontSize: 10 }}
                    />
                    <Tooltip content={renderTooltip} coordinate={{ x: -1000, y: -1000 }} />
                    <Line
                        type="monotone"
                        dataKey="ele"
                        stroke="#8884d8"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 5 }}
                    />
                    {markerReferenceDots.map((dot, index) => (
                        <ReferenceDot
                            key={`dot-${index}`}
                            x={dot.distance}
                            y={dot.ele}
                            r={4}
                            fill="#f00"
                            stroke="none"
                            ifOverflow="extendDomain"
                            label={{ value: dot.label, position: 'bottom', dy: 15, fontSize: 10 }}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default ElevationProfilePanel; 